import { PlusIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import {
    EmptyList,
    ErrorState,
    ProtectedButton,
    Table,
    TableSkeleton,
} from '@src/components';
import { DeleteModal } from '@src/components/DeleteModal/DeleteModal';
import { usePageTitle } from '@src/contexts';
import { useDisplayListLogic, useLocalStorageValue } from '@src/hooks';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import ChangeUserRoleModal from './changeUserRoleModal/ChangeUserRoleModal';
import { RenderColumns } from './columns';
import { UsersFilter } from './components';
import { useUsersList } from './hooks';
import NewUserModal from './newUserModal/NewUserModal';

interface UsersListParams {
    roleId?: string | null;
}

export const UsersList = ({ roleId = null }: UsersListParams) => {
    const { t } = useTranslation();

    const { setBreadcrumbs } = usePageTitle();
    useEffect(() => {
        setBreadcrumbs([{ title: t('pages.users.title') }]);
    }, []);

    const storageKey = 'usersHiddenColumns';
    const [hiddenColumns, setHiddenColumns] = useLocalStorageValue<string[]>(
        storageKey,
        []
    );

    const {
        data,
        isLoading,
        isFetching,
        isDeleting,
        isError,
        searchText,
        selectedUser,
        openFilter,
        openUserChangeRoleModal,
        openUserDeleteModal,
        openUserFormModal,
        filters,
        sortBy,
        sortOrder,
        confirmDelete,
        setOpenUserChangeRoleModal,
        setOpenUserDeleteModal,
        setOpenUserFormModal,
        setOpenFilter,
        handleAddUser,
        handleActionClick,
        handleChangePagination,
        handleFilterChange,
        handleSearchChange,
        handleRemoveAllFilters,
        handleSortChange,
    } = useUsersList(roleId);

    const columns = RenderColumns({
        t,
        sorterState: { sortBy, sortOrder, onSortChange: handleSortChange },
        roleId,
        hiddenColumns,
        setHiddenColumns,
        onActionClick: handleActionClick,
    });

    const { showTable, showEmptyState: originalShowEmptyState } =
        useDisplayListLogic({
            data: data?.data.items ?? [],
            originalData: [],
            isLoading,
            isFetching,
            isError: isError,
            searchText: searchText,
        });

    const showErrorState = isError && !isLoading && !isFetching;

    const hasErrorOrLoading =
        isError || (isLoading && !data?.data?.items?.length);
    const showEmptyState = originalShowEmptyState && !hasErrorOrLoading;

    const totalBaseColumns =
        columns.filter((c) => c.key !== 'actions').length +
        hiddenColumns.length;
    const allHidden = hiddenColumns.length >= totalBaseColumns;

    return (
        <>
            {isLoading && <TableSkeleton />}
            {showTable && !showErrorState && (
                <Table
                    title={
                        <div className="flex gap-2 items-center">
                            <span>
                                {`You have ${data?.data.pagination.total ?? 0}
                                ${t('pages.users.title')}`}
                            </span>
                        </div>
                    }
                    searchPlaceholder={t('general.searchMinChars')}
                    scroll={{ x: 1 }}
                    loading={isLoading || isFetching}
                    columns={columns}
                    actions={
                        <ProtectedButton
                            module="UserManagement"
                            labelKey="pages.users.buttons.newUser"
                            icon={<PlusIcon width={16} height={16} />}
                            onClick={handleAddUser}
                        />
                    }
                    dataSource={data?.data.items || []}
                    onPageChange={handleChangePagination}
                    paginationProps={{
                        total: data?.data?.pagination?.total ?? 0,
                        current: data?.data?.pagination?.page,
                        pageSize: data?.data?.pagination?.limit,
                    }}
                    activeFilter={true}
                    filterOpen={true}
                    filters={filters}
                    onClearFilters={handleRemoveAllFilters}
                    setFilterOpen={() => setOpenFilter(true)}
                    onSearch={handleSearchChange}
                    searchValue={searchText}
                    className="[&_.ant-table-content]:overflow-x-auto"
                    allColumnsHidden={allHidden}
                    onShowAllColumns={() => setHiddenColumns([])}
                />
            )}

            {showEmptyState && !showErrorState && (
                <EmptyList
                    message={t('general.emptyListMessage', {
                        name: t('pages.users.title'),
                    })}
                >
                    <ProtectedButton
                        module="UserManagement"
                        labelKey="pages.users.buttons.newUser"
                        icon={<PlusIcon width={16} height={16} />}
                        onClick={handleAddUser}
                    />
                </EmptyList>
            )}

            {showErrorState && (
                <ErrorState
                    message={t('errors.loadingTrouble', {
                        name: t('pages.users.title').toLowerCase(),
                    })}
                />
            )}
            {openUserFormModal && (
                <NewUserModal
                    open={openUserFormModal}
                    onClose={() => setOpenUserFormModal(false)}
                />
            )}
            {openUserChangeRoleModal && selectedUser && (
                <ChangeUserRoleModal
                    open={openUserChangeRoleModal}
                    onClose={() => setOpenUserChangeRoleModal(false)}
                    user={selectedUser}
                />
            )}
            {openFilter && (
                <UsersFilter
                    initialFormData={filters}
                    onChange={handleFilterChange}
                    onClear={handleRemoveAllFilters}
                    onClose={() => setOpenFilter(false)}
                />
            )}
            {openUserDeleteModal && selectedUser && (
                <DeleteModal
                    isOpen={openUserDeleteModal}
                    isDeleting={isDeleting}
                    icon={<UserGroupIcon className="w-6 h-6" />}
                    pageTitle={t('pages.users.subTitle')}
                    itemName={selectedUser.fullName}
                    deleteMessage={
                        selectedUser?.email
                            ? t('keyTerms.deleteMessage', {
                                  name: selectedUser.email,
                              })
                            : undefined
                    }
                    onClose={() => setOpenUserDeleteModal(false)}
                    onDelete={confirmDelete}
                />
            )}
        </>
    );
};
